import React, { useEffect, useRef, useState, useCallback } from "react";
import { message, Modal } from "antd";
import { WarningOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import "./MedicalRecordUpload.css";

import uploadIcon from "../../../assets/icons/Icon_upload.png";
import pdfIcon from "../../../assets/icons/pdf-icon.svg";

import AnalysisProgressModal from "../AnalysisProgressModal/AnalysisProgressModal";
import ResultsTableModal from "../ResultsTableModal/ResultsTableModal";
import DocumentTypeModal from "../DocumentTypeModal/DocumentTypeModal";

import { selectProcessLoading } from "../../redux/selectors/extractionSelectors";

import useExtraction from "../../hooks/useExtraction";

const IMG_EXTS = [".png", ".jpg", ".jpeg", ".webp", ".bmp", ".tiff"];

const BILAN_TYPES = [
  "BILAN_BIOCHIMIQUE",
  "BILAN_SANGUIN",
  "BILAN",
  "NFS",
  "HEMOGRAMME",
  "BILAN_LIPIDIQUE",
  "BILAN_HEPATIQUE",
  "BILAN_RENAL",
  "BILAN_THYROIDIEN",
  "BIOCHIMIQUE",
  "SANGUIN",
];

const DISABLE_CUSTOM_MODAL_FLOW = false;

export const normalizeParametres = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((param) => ({
        ...param,
        nom: param.nom ?? param.name ?? param.parametre ?? "-",
        valeur: param.valeur ?? param.value ?? "-",
        unite: param.unite ?? param.unit ?? "-",
        normale: param.normale ?? param.normal ?? param.reference ?? "-",
        statut: param.statut ?? param.status ?? "NORMAL",
      }));
  }

  if (typeof value === "object") {
    return Object.entries(value).map(([nom, details]) => ({
      nom,
      ...(typeof details === "object"
        ? details
        : {
            valeur: details,
          }),
    }));
  }

  return [];
};

export const extractAnalysisData = (result) => {
  const response = result?.data || result || {};
  const savedDoc = response?.data || response;

  const rawDocType =
    savedDoc?.type_document ||
    savedDoc?.classification?.type ||
    savedDoc?.classification?.document_type ||
    response?.type_document ||
    response?.classification?.type ||
    response?.classification?.document_type ||
    response?.collection ||
    "AUTRE";

  const docType = String(rawDocType || "AUTRE").toUpperCase();

  let parametres = [];

  parametres = normalizeParametres(savedDoc?.parametres);

  if (!parametres.length) {
    parametres = normalizeParametres(response?.parametres);
  }

  if (!parametres.length) {
    parametres = normalizeParametres(
      savedDoc?.extraction?.structured?.analyses?.parametres
    );
  }

  if (!parametres.length) {
    parametres = normalizeParametres(
      response?.extraction?.structured?.analyses?.parametres
    );
  }

  if (!parametres.length) {
    parametres = normalizeParametres(savedDoc?.interpretations?.parametres);
  }

  const isBilan = BILAN_TYPES.some((type) => docType.includes(type));

  return {
    response,
    savedDoc,
    docType,
    parametres,
    isBilan,
    hasParams: Array.isArray(parametres) && parametres.length > 0,
    analysisIncomplete: isBilan && parametres.length === 0,
  };
};

const MedicalRecordUpload = ({
  userId = null,
  subprofileId = null,
  viewMode = "patient",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const processLoading = useSelector(selectProcessLoading);

  const {
    selectedFile,
    previewUrl,
    handleFileSelect,
    resetFileSelection,
    isStorageFull,
    isStorageAlmostFull,
    storageDocumentCount,
    storageMaxFiles,
    storageAvailableFiles,
    handleUploadAndAnalyze,
    refreshAllData,
    getUserStorage,
    updateParameters,
  } = useExtraction(userId, { subprofileId });

  const [isEditing, setIsEditing] = useState(false);
  const [editedBaseName, setEditedBaseName] = useState("");
  const [extension, setExtension] = useState("");

  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [analysisIncomplete, setAnalysisIncomplete] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [detectedDocType, setDetectedDocType] = useState("");

  const analysisSteps = [
    t("upload.steps.classification"),
    t("upload.steps.ocr"),
    t("upload.steps.extraction"),
    t("upload.steps.normalize"),
    t("upload.steps.anonymization"),
  ];

  const [steps, setSteps] = useState(
    analysisSteps.map((label) => ({ label, completed: false }))
  );

  useEffect(() => {
    getUserStorage();
  }, [getUserStorage]);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const checkStorageBeforeUpload = (file) => {
    if (!file) return false;

    if (storageAvailableFiles <= 0) {
      Modal.error({
        title: t("upload.quotaExceeded"),
        content: (
          <div>
            <p>
              <strong>{t("upload.used")}:</strong>{" "}
              {storageDocumentCount} / {storageMaxFiles} files
            </p>
            <p>
              <strong>{t("upload.available")}:</strong>{" "}
              0 files
            </p>
            <p style={{ marginTop: 16, color: "#ff4d4f" }}>
              ⚠️ {t("upload.notEnoughSpace")}
            </p>
            <p>{t("upload.deleteToFreeSpace")}</p>
          </div>
        ),
        okText: t("upload.ok"),
      });
      return false;
    }

    const newUsagePercent =
      ((storageDocumentCount + 1) / Math.max(storageMaxFiles, 1)) * 100;

    if (newUsagePercent >= 90 && newUsagePercent < 100) {
      Modal.confirm({
        title: t("upload.highStorageTitle"),
        icon: <WarningOutlined style={{ color: "#faad14" }} />,
        content: (
          <div>
            <p>
              {t("upload.highStorageMessage", {
                size: 1,
                percent: newUsagePercent.toFixed(1),
              })}
            </p>
            <p>{t("upload.continueQuestion")}</p>
          </div>
        ),
        okText: t("upload.yesUpload"),
        cancelText: t("upload.cancel"),
        onOk: () => {
          if (handleFileSelect(file) === false) return;
          const match = file.name.match(/^(.*?)(\.[^.]*)?$/);
          setEditedBaseName((match?.[1] || file.name).trim());
          setExtension((match?.[2] || "").toLowerCase());
        },
      });
      return false;
    }

    return true;
  };

  const primeFileLocal = (file) => {
    if (!file) return;

    const canProceed = checkStorageBeforeUpload(file);
    if (!canProceed) return;

    if (handleFileSelect(file) === false) return;

    const match = file.name.match(/^(.*?)(\.[^.]*)?$/);
    setEditedBaseName((match?.[1] || file.name).trim());
    setExtension((match?.[2] || "").toLowerCase());
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();

    if (isStorageFull) {
      message.error(t("upload.storageFullMessage"));
      return;
    }

    primeFileLocal(e.dataTransfer?.files?.[0]);
  };

  const handleFileChange = (e) => {
    if (isStorageFull) {
      message.error(t("upload.storageFullMessage"));
      return;
    }

    primeFileLocal(e.target.files?.[0]);
  };

  const handleUploadClick = () => {
    if (isStorageFull) {
      Modal.error({
        title: t("upload.storageFullTitle"),
        content: (
          <div>
            <p style={{ color: "#ff4d4f", fontWeight: 600 }}>
              ⚠️ {t("upload.reachedLimit")}
            </p>
            <p>
              <strong>{t("upload.used")}:</strong>{" "}
              {storageDocumentCount} / {storageMaxFiles} files
            </p>
            <p>{t("upload.deleteBeforeUpload")}</p>
          </div>
        ),
        okText: t("upload.ok"),
      });
      return;
    }

    fileInputRef.current?.click();
  };

  const handleSaveName = () => {
    const cleaned = (editedBaseName || "")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "-");

    setEditedBaseName(cleaned);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveName();
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setIsEditing(false);
    }
  };

  const withRenamedFile = () => {
    if (!selectedFile) return null;

    const finalName = editedBaseName
      ? `${editedBaseName}${extension || ""}`
      : selectedFile.name;

    if (finalName === selectedFile.name) return selectedFile;

    return new File([selectedFile], finalName, {
      type: selectedFile.type,
    });
  };

  const resetLocalState = () => {
    resetFileSelection();
    setEditedBaseName("");
    setExtension("");
    setAnalysisResult(null);
    setDetectedDocType("");
    setAnalysisIncomplete(false);
  };

  const handleReuploadClick = () => {
    resetLocalState();
    setShowResultsModal(false);
    setShowDocTypeModal(false);
    setShowProgressModal(false);

    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleAnalyzeClick = async () => {
    const fileForAnalysis = withRenamedFile();

    if (!fileForAnalysis) {
      message.error(t("upload.noFileSelected"));
      return;
    }

    if (DISABLE_CUSTOM_MODAL_FLOW) {
      try {
        await handleUploadAndAnalyze({
          file: fileForAnalysis,
          device: "auto",
          redactionMethod: "blackout",
        });

        await refreshAllData();
        await getUserStorage();
        resetLocalState();

        message.success(t("upload.saveSuccess"));
      } catch (error) {
        message.error(error?.message || t("upload.analysisFailed"));
      }

      return;
    }

    setSteps(analysisSteps.map((label) => ({ label, completed: false })));
    setCurrentStep(0);
    setAnalysisResult(null);
    setDetectedDocType("");
    setAnalysisIncomplete(false);
    setShowProgressModal(true);
    setShowResultsModal(false);
    setShowDocTypeModal(false);

    const stepDuration = 1500;
    let currentIdx = 0;

    const progressInterval = setInterval(() => {
      setSteps((prev) =>
        prev.map((step, idx) =>
          idx === currentIdx ? { ...step, completed: true } : step
        )
      );

      setCurrentStep(currentIdx);
      currentIdx += 1;

      if (currentIdx >= analysisSteps.length) {
        clearInterval(progressInterval);
      }
    }, stepDuration);

    try {
      const result = await handleUploadAndAnalyze({
        file: fileForAnalysis,
        device: "auto",
        redactionMethod: "blackout",
        showMessages: false,
      });

      if (!result) {
        throw new Error(t('upload.analysisFailed'));
      }

      clearInterval(progressInterval);

      setSteps(analysisSteps.map((label) => ({ label, completed: true })));
      setCurrentStep(analysisSteps.length - 1);

      const {
        response,
        savedDoc,
        docType,
        parametres,
        isBilan,
        hasParams,
        analysisIncomplete: didAnalysisFail,
      } = extractAnalysisData(result);

      setDetectedDocType(docType);
      setAnalysisIncomplete(didAnalysisFail);

      await refreshAllData();
      await getUserStorage();

      await new Promise((resolve) => setTimeout(resolve, 800));
      setShowProgressModal(false);

      if (isBilan && hasParams) {
        setAnalysisResult({
          documentType: docType,
          parametres,
          documentData: savedDoc,
          data: savedDoc,
          documentId: response?.document_id || savedDoc?._id || savedDoc?.id,
          collection:
            response?.collection || savedDoc?.collection || "bilan",
          version: savedDoc?.version ?? response?.version ?? 1,
        });

        setTimeout(() => {
          setShowResultsModal(true);
        }, 300);

        return;
      }

      if (isBilan && !hasParams) {
        message.warning(t("upload.bilanNoParams"));
      }

      setTimeout(() => {
        setShowDocTypeModal(true);
      }, 300);
    } catch (error) {
      clearInterval(progressInterval);
      setShowProgressModal(false);

      if (error.response?.status === 413 || error.status === 413) {
        const errorData = error.response?.data?.detail || error.detail;

        Modal.error({
          title: t("upload.quotaExceeded"),
          content: (
            <div>
              <p>{errorData?.message || t("upload.quotaExceededMessage")}</p>

              {errorData && (
                <>
                  <p>
                    <strong>{t("upload.used")}:</strong>{" "}
                    {errorData.document_count ?? storageDocumentCount} /{" "}
                    {errorData.max_files ?? storageMaxFiles} files
                  </p>
                  <p>
                    <strong>{t("upload.quota")}:</strong>{" "}
                    {errorData.max_files ?? storageMaxFiles} files
                  </p>
                  <p>
                    <strong>{t("upload.available")}:</strong>{" "}
                    {errorData.available_files ?? storageAvailableFiles} files
                  </p>
                </>
              )}
            </div>
          ),
        });
      } else {
        message.error(error?.message || t("upload.analysisFailed"));
      }
    }
  };

  const handleCloseProgress = () => {
    setShowProgressModal(false);
  };

  const handleCloseResults = () => {
    setShowResultsModal(false);
    resetLocalState();
  };

  const handleSaveResults = async (saveData) => {
    try {
      if (saveData?.modifications?.length > 0) {
        const result = await updateParameters(
          analysisResult?.collection,
          analysisResult?.documentId,
          saveData,
          analysisResult?.version,
        );

        if (!result) {
          throw new Error(t("upload.saveFailed"));
        }
      }

      await refreshAllData();
      await getUserStorage();

      message.success(t("upload.saveSuccess"));

      setShowResultsModal(false);
      resetLocalState();
    } catch {
      message.error(t("upload.saveFailed"));
    }
  };

  const handleCloseDocType = () => {
    setShowDocTypeModal(false);
    resetLocalState();
  };

  const handleViewRecords = (documentType) => {
    setShowDocTypeModal(false);

    const type = documentType || detectedDocType || "AUTRE";
    navigate(
      viewMode === "doctor" && userId
        ? `/doctor/patients/${userId}/records/${type}`
        : `/records/${type}`
    );
  };

  const isImage = selectedFile && IMG_EXTS.includes(extension.toLowerCase());
  const canAnalyze = !processLoading && selectedFile && !isStorageFull;

  return (
    <div className="medical-upload-container">
      {isStorageAlmostFull && !isStorageFull && (
        <div className="storage-warning-banner">
          <WarningOutlined style={{ fontSize: 18, color: "#faad14" }} />
          <span>
            {t("upload.storageAlmostFull", {
              percent: (
                (storageDocumentCount / Math.max(storageMaxFiles, 1)) * 100
              ).toFixed(1),
            })}
          </span>
        </div>
      )}

      {isStorageFull && (
        <div className="storage-full-banner">
          <CloseCircleOutlined style={{ fontSize: 18, color: "#ff4d4f" }} />
          <span>{t("upload.storageFullBanner", { quota: storageMaxFiles })}</span>
        </div>
      )}

      {!selectedFile ? (
        <>
          <div
            className="drop-zone"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleUploadClick}
            style={{
              opacity: isStorageFull ? 0.5 : 1,
              cursor: isStorageFull ? "not-allowed" : "pointer",
            }}
          >
            <div className="drop-inner">
              <img
                src={uploadIcon}
                alt={t("upload.uploadAlt")}
                className="upload-icon"
                width="48"
                height="48"
              />
              <p className="drop-text">
                <span>{t("upload.dragDrop")}</span>
                <br />
                <span>{t("upload.fileHere")}</span>
              </p>
            </div>

            <input
              ref={fileInputRef}
              id="fileInput"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.bmp,.tiff"
              onChange={handleFileChange}
              style={{ display: "none" }}
              disabled={isStorageFull}
            />
          </div>

          <div className="info-section">
            <h2>{t("upload.addRecord")}</h2>
            <p>{t("upload.addRecordDesc")}</p>

            <button
              className="upload-button"
              onClick={handleUploadClick}
              disabled={isStorageFull}
              style={{
                opacity: isStorageFull ? 0.5 : 1,
                cursor: isStorageFull ? "not-allowed" : "pointer",
              }}
            >
              {isStorageFull
                ? t("upload.storageFull")
                : t("upload.uploadRecord")}
            </button>
          </div>
        </>
      ) : (
        <div className="extraction-result-card">
          <button
            className="card-top-btn"
            onClick={() => setIsEditing(true)}
            title={t("upload.editFilename")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 20h9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="card-row">
            <div className="file-preview">
              {isImage && previewUrl ? (
                <img
                  src={previewUrl}
                  alt={t("upload.preview")}
                  className="file-thumb"
                />
              ) : (
                <img src={pdfIcon} alt={t("upload.file")} className="pdf-icon" />
              )}
            </div>

            <div className="extraction-details">
              <div className="file-edit-line">
                <span className="label-with-icon">
                  <span className="label-text">{t("upload.fileLabel")}:</span>
                </span>

                {isEditing ? (
                  <div className="file-edit-wrap">
                    <input
                      ref={inputRef}
                      type="text"
                      value={editedBaseName}
                      onChange={(e) => setEditedBaseName(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="file-edit-input"
                      aria-label={t("upload.editFilename")}
                    />

                    <span className="file-ext">{extension}</span>

                    <div className="inline-actions">
                      <button className="btn-save" onClick={handleSaveName}>
                        {t("upload.save")}
                      </button>

                      <button
                        className="btn-cancel"
                        onClick={() => setIsEditing(false)}
                      >
                        {t("upload.cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="file-name-static">
                    {editedBaseName ||
                      selectedFile?.name?.replace(/\.[^.]+$/, "") ||
                      ""}
                    <span className="file-ext">
                      {extension ||
                        selectedFile?.name?.match(/(\.[^.]+)$/)?.[1] ||
                        ""}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="actions">
            <button
              className="reupload-btn"
              onClick={handleReuploadClick}
              disabled={processLoading}
            >
              {t("upload.chooseAnother")}
            </button>

            <button
              className="analyze-btn"
              onClick={handleAnalyzeClick}
              disabled={!canAnalyze}
            >
              {processLoading ? t("upload.analyzing") : t("upload.analyzeAI")}
            </button>
          </div>
        </div>
      )}

      {!DISABLE_CUSTOM_MODAL_FLOW && (
        <>
          <AnalysisProgressModal
            open={showProgressModal}
            onClose={handleCloseProgress}
            title={t("upload.analyzingTitle")}
            steps={steps.map((s) => s.label)}
            currentStep={currentStep}
            autoplay={false}
            showResults={false}
          />

          {showResultsModal && analysisResult && (
            <ResultsTableModal
              visible={showResultsModal}
              result={analysisResult}
              onClose={handleCloseResults}
              onSave={handleSaveResults}
              readOnly={viewMode === "doctor"}
            />
          )}

          {showDocTypeModal && (
            <DocumentTypeModal
              open={showDocTypeModal}
              title={t("upload.saveSuccessTitle")}
              analysisIncomplete={analysisIncomplete}
              documentType={detectedDocType}
              onClose={handleCloseDocType}
              onViewRecords={handleViewRecords}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MedicalRecordUpload;
